import { DateTime } from "@dashboard/components/Date";
import Money from "@dashboard/components/Money";
import { OrderListQuery } from "@dashboard/graphql";
import { getStatusColor, PillStatusType } from "@dashboard/misc";
import { RelayToFlat } from "@dashboard/types";
import { Box, Skeleton, Text, useTheme } from "@saleor/macaw-ui-next";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { getCustomerCellContent, getOrderPaymentStage, getOrderProgressStage } from "./datagrid";
import { messages } from "./messages";

type OrderListItem = RelayToFlat<NonNullable<OrderListQuery["orders"]>>[number];
// Draft orders share these fields, so the same list serves the draft list.
type OrderListMobileItem = Pick<
  OrderListItem,
  "id" | "number" | "created" | "total" | "billingAddress" | "userEmail"
> &
  Partial<OrderListItem>;

// `rowAnchor` URLs carry a query string (e.g. "/orders/ID?"), so split it for
// a router location that can also carry the back-link state.
const toLocation = (url: string, state: unknown) => {
  const [pathname, search] = url.split("?");

  return { pathname, search: search ? `?${search}` : "", state };
};

// Same colours as the datagrid's pill cells.
const StatusChip = ({ label, status }: { label: string; status: PillStatusType }) => {
  const { theme } = useTheme();
  const color = getStatusColor({ status, currentTheme: theme });

  return (
    <Box
      as="span"
      paddingX={2}
      paddingY={0.5}
      borderRadius={4}
      __backgroundColor={color.base}
      __color={color.text}
      __whiteSpace="nowrap"
    >
      <Text size={2} fontWeight="medium" color="inherit">
        {label}
      </Text>
    </Box>
  );
};

interface OrderListMobileListProps {
  orders: OrderListMobileItem[] | undefined;
  loading: boolean;
  rowAnchor?: (id: string) => string;
  linkState?: unknown;
  // Order list shows progress + payment pills; drafts have neither.
  showStatus?: boolean;
}

const OrderListMobileListItem = ({
  order,
  showStatus,
}: {
  order: OrderListMobileItem;
  showStatus: boolean;
}) => {
  const intl = useIntl();
  const stage = showStatus ? getOrderProgressStage(order as OrderListItem, intl) : null;
  const payment = showStatus ? getOrderPaymentStage(order as OrderListItem, intl) : null;
  const customer = getCustomerCellContent(order as OrderListItem).data;

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      <Box display="flex" justifyContent="space-between" alignItems="baseline" gap={2}>
        <Text size={4} fontWeight="bold">
          #{order.number}
        </Text>
        {order.total?.gross && (
          <Text size={4} fontWeight="bold">
            <Money money={order.total.gross} />
          </Text>
        )}
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="baseline" gap={2}>
        <Text size={3} style={{ overflowWrap: "anywhere", minWidth: 0 }}>
          {customer}
        </Text>
        <Text size={2} color="default2" __whiteSpace="nowrap">
          <DateTime date={order.created} plain />
        </Text>
      </Box>
      {(stage || payment) && (
        <Box display="flex" flexWrap="wrap" gap={1.5} marginTop={0.5}>
          {stage && <StatusChip label={stage.localized} status={stage.status} />}
          {payment && <StatusChip label={payment.localized} status={payment.status} />}
        </Box>
      )}
    </Box>
  );
};

/**
 * Phone-friendly order list: one tappable card per order. The canvas datagrid
 * needs horizontal scrolling to reach status/total columns on a narrow screen.
 */
export const OrderListMobileList = ({
  orders,
  loading,
  rowAnchor,
  linkState,
  showStatus = true,
}: OrderListMobileListProps) => {
  const intl = useIntl();

  if (loading && !orders?.length) {
    return (
      <Box display="flex" flexDirection="column" gap={4} paddingX={4} paddingY={4}>
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </Box>
    );
  }

  if (!orders?.length) {
    return (
      <Box paddingX={4} paddingY={6}>
        <Text size={3} color="default2">
          {intl.formatMessage(messages.emptyText)}
        </Text>
      </Box>
    );
  }

  return (
    <Box as="ul" margin={0} padding={0} data-test-id="order-list-mobile">
      {orders.map(order => (
        <Box
          as="li"
          key={order.id}
          listStyleType="none"
          borderBottomStyle="solid"
          borderBottomWidth={1}
          borderColor="default1"
        >
          {rowAnchor ? (
            <Link
              to={toLocation(rowAnchor(order.id), linkState)}
              style={{ display: "block", padding: "12px 16px", color: "inherit" }}
              data-test-id="order-list-mobile-item"
            >
              <OrderListMobileListItem order={order} showStatus={showStatus} />
            </Link>
          ) : (
            <Box paddingX={4} paddingY={3}>
              <OrderListMobileListItem order={order} showStatus={showStatus} />
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};
